// frontend/src/components/SessionNameField/SessionNameField.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import apiClient from '../../apiClient';
import './SessionNameField.css';

/**
 * SessionNameField - Editable session name input with autosave
 *
 * @param {string} sessionId - The session ID to update
 * @param {string} initialName - Initial session name (from loaded session)
 * @param {function} onNameChange - Callback when name changes (optional, for parent sync)
 * @param {string} placeholder - Placeholder text
 */
function SessionNameField({
  sessionId,
  initialName = '',
  onNameChange,
  placeholder = 'Skriv sessionsnamn...',
  className = ''
}) {
  const [localName, setLocalName] = useState(initialName || '');
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // 'saved' | 'error' | null
  const saveTimeoutRef = useRef(null);
  const statusTimeoutRef = useRef(null);

  // Sync with initialName when it changes (e.g., loading from backend)
  useEffect(() => {
    if (initialName !== undefined && initialName !== localName) {
      setLocalName(initialName || '');
    }
  }, [initialName]);

  // Debounced save function
  const saveToBackend = useCallback(async (name) => {
    if (!sessionId) return;

    setIsSaving(true);
    setSaveStatus(null);

    try {
      await apiClient.patch(`/recorder/sessions/${sessionId}/update_name`, {
        session_name: name
      });
      setSaveStatus('saved');

      // Clear status after 2 seconds
      if (statusTimeoutRef.current) {
        clearTimeout(statusTimeoutRef.current);
      }
      statusTimeoutRef.current = setTimeout(() => {
        setSaveStatus(null);
      }, 2000);
    } catch (err) {
      console.error('Failed to save session name:', err);
      setSaveStatus('error');
    } finally {
      setIsSaving(false);
    }
  }, [sessionId]);

  // Handle input change with debounce
  const handleChange = (e) => {
    const newValue = e.target.value;
    setLocalName(newValue);

    // Notify parent if callback provided
    if (onNameChange) {
      onNameChange(newValue);
    }

    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Set new timeout for autosave (1 second debounce)
    saveTimeoutRef.current = setTimeout(() => {
      saveToBackend(newValue);
    }, 1000);
  };

  // Save immediately on blur
  const handleBlur = () => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveToBackend(localName);
  };

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      if (statusTimeoutRef.current) {
        clearTimeout(statusTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className={`session-name-field ${className}`.trim()}>
      <input
        type="text"
        className="session-name-input"
        value={localName}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        maxLength={255}
      />
      <div className="session-name-status">
        {isSaving && <span className="status-saving">Sparar...</span>}
        {saveStatus === 'saved' && <span className="status-saved">Sparat</span>}
        {saveStatus === 'error' && <span className="status-error">Fel vid sparning</span>}
      </div>
    </div>
  );
}

export default SessionNameField;
